import { PaymentIntent } from "@stripe/stripe-js";
import { makeAutoObservable } from "mobx";
import toast from "react-hot-toast";
import { ConsultationResponse } from "src/models/consultation";
import { Event } from "src/models/event";
import { SecretIndent } from "src/models/paymentIndent";
import type { ServiceModel } from "src/models/service";
import { SubServiceModel } from "src/models/subService";
import { RegisterationFormValues } from "src/sections/service/patient-registeration";
import { Questionaries } from "src/sections/service/questionaries";
import { Certificate } from "src/sections/service/special-service/certificate";
import { Drug } from "src/sections/service/special-service/drug-search";
import { GPDetailsFormModel } from "src/sections/service/special-service/gp-details";
import { getDoctorHistory, getScheduledDetails } from "src/services/doctor/doctorService";
import { createConsultation, getPaymentIndent, getServiceById } from "src/services/home/homeService";

export class ServiceStore {
  

  service: ServiceModel | null = null;

  questionaries: Questionaries = new Questionaries([], '');

  doctorAvailability : Event |null = null;

  loading: boolean = true;

  contentLoading: boolean = false;

  secretIndent: SecretIndent| null = null;

  personalDetails: RegisterationFormValues = new RegisterationFormValues(); 

  step: number = 0;

  patientId: number| null = null;

  selectedService: SubServiceModel | null = null;

  paymentIndent: PaymentIntent | null = null;

  certificates: Certificate = new Certificate('', '', '');

  selectedDrugs: Drug[] = [];

  gpDetails: GPDetailsFormModel = new GPDetailsFormModel({});

  isPaymentSuccess :boolean = false;

  consultationResponse: ConsultationResponse | null = null;


  constructor() {
    makeAutoObservable(this);
  }

  async getServiceById(id: string){
    try{
        this.toggleLoading(true);
        const response = await getServiceById(id);
        if(response.ok){
            this.service = response.data;
        }else{
            toast.error(response.message);
        }
    }catch{
       toast.error('something went wrong!'); 
    }finally{
        this.toggleLoading(false); 
    }
  }

  setService(service: SubServiceModel | null){
    this.selectedService = service;
  }

  setDoctorAvailabilty(event: Event | null){
    this.doctorAvailability = event;
  }

  toggleLoading(value:boolean){
    this.loading = value;
  }

  showloader(value:boolean){
    this.contentLoading = value;
  }

  goBack(){
    if(this.step !== 0){
        this.step = this.step -1;
    }
  }

  updateStep(isBack = false) {
    this.step = isBack ?  this.step -1 : this.step+1;
  }

  updatePatient(id: number | null) {
    this.patientId = id;
  }

  updatePersonalDetails(personalDetail: RegisterationFormValues){
    this.personalDetails = personalDetail;
    this.step = this.step + 1;
  }

  updateQuestionaries(questions: Questionaries){
    this.questionaries = questions;
    this.step = this.step + 1;
  }

  updateCertificate(certificate: Certificate){
    this.certificates = certificate;
    this.step = this.step + 1;
  }

  async updatePayment(payment: PaymentIntent){
    const response = await createConsultation({
      selectedService: {...this.selectedService, createDate: new Date().valueOf()},
      serviceId: this.service?.id,
      questionaries: this.questionaries,
      personalDetails: this.patientId !==null ? null: this.personalDetails,
      paymentIndent: payment,
      certificates: this.certificates,
      selectedDrugs: this.selectedDrugs,
      doctorAvailability :this.doctorAvailability,
      patientId : this.patientId,
      gpDetails: this.gpDetails
    });
    
    if (response.ok && response.data) {
      this.consultationResponse = response.data as ConsultationResponse;
    }
    
    return response;
  }

  setPaymentSuccess(payment: PaymentIntent) {
    this.paymentIndent = payment;
    this.isPaymentSuccess = true;
  }

  setPaymentFailed() {
    this.paymentIndent = {
      id: 'failed',
      status: 'canceled',
      amount: 0,
      currency: 'aud',
      client_secret: '',
      created: Date.now(),
      livemode: false,
      object: 'payment_intent',
      payment_method_types: [],
      setup_future_usage: null,
      automatic_payment_methods: null,
      capture_method: 'automatic',
      confirmation_method: 'automatic',
      description: null,
      last_payment_error: null,
      next_action: null,
      receipt_email: null,
      statement_descriptor: null,
      statement_descriptor_suffix: null,
      transfer_data: null,
      transfer_group: null,
      canceled_at: null,
      cancellation_reason: null,
      payment_method: null,
      shipping: null
    } as PaymentIntent;
    this.isPaymentSuccess = false;
  }

  setQuestionaries(){
    this.questionaries.questions = this.service?.questions?.split('|').map((qes) => {return {question: qes, answer:''}}) || [];
  }

  updatePaymentResult(value:boolean){
    this.isPaymentSuccess = value;
  }

  updateDrugs(drugs : Drug[]){
    this.selectedDrugs = drugs;
    this.step = this.step + 1;
  }

  updateGPDetails(gpDetails : GPDetailsFormModel){
    this.gpDetails = gpDetails;
    this.step = this.step + 1;
  }

  reset() {
    this.loading = true;
    this.service = null;
    this.selectedService = null;
    this.personalDetails = new RegisterationFormValues(); 
    this.step = 0;
    this.patientId = null;
    this.certificates = new Certificate('', '', '');
    this.paymentIndent = null;
    this.secretIndent = null;
    this.questionaries = new Questionaries([], '');
    this.selectedDrugs = [];
    this.doctorAvailability = null;
    this.gpDetails =  new GPDetailsFormModel({});
    this.consultationResponse = null;
  }

  async getPaymentAmount(){
      const amount = this.selectedService?.cost ?? this.service?.cost ?? 0;
      const response = await getPaymentIndent(amount);
      if(response.ok){
        this.secretIndent = response.data;
      }else{
        toast.error('Error while Fetching payment informtion');
      }
  }

async checkDoctorSlotAvailability(): Promise<{ ok: boolean; message?: string }> {

  // ✅ Only for Scheduled Appointment
  if (this.selectedService?.title !== 'Schedule Appointment') {
    return { ok: true }; // 🔥 DO NOT BLOCK other services
  }

  if (!this.doctorAvailability) {
    return { ok: false, message: 'Doctor slot not selected' };
  }

  const { doctorId, startTime } = this.doctorAvailability;

  const response = await getScheduledDetails(doctorId.toString());

  if (!response.ok) {
    return {
      ok: false,
      message: response.message ?? 'Unable to verify slot availability',
    };
  }

  const existingSlots = response.data ?? [];

  const isSlotAlreadyBooked = existingSlots.some(
    slot => slot.startTime === startTime
  );

  if (isSlotAlreadyBooked) {
    return {
      ok: false,
      message: 'This doctor slot has already been booked by another patient',
    };
  }

  return { ok: true };
}



}